import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Withdrawal.css";
import Swal from "sweetalert2";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getApiBaseUrl } from "../utils/apiConfig";

const API = getApiBaseUrl();
const getAdminHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('jwt_token') || localStorage.getItem('token')}`
});

/* -------- tiny count-up for card numbers -------- */
function CountUp({ to = 0, duration = 900, prefix = "", suffix = "" }) {
  const [val, setVal] = useState(0);
  const start = useRef(null);
  const target = Number(to || 0);

  useEffect(() => {
    start.current = null;
    let raf = 0;

    const step = (ts) => {
      if (!start.current) start.current = ts;
      const p = Math.min(1, (ts - start.current) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);

  return (
    <>
      {prefix}
      {money
        ? val.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
        : Math.round(val).toLocaleString()}
      {suffix}
    </>
  );
}

/* -------- helpers -------- */
const money = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0 });
const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
    : "—";

// Mask account number except last 4 digits
// function maskAccountNumber(acc) {
//   if (!acc) return "—";
//   const s = String(acc);
//   if (s.length <= 4) return s;
//   return "**** " + s.slice(-4);
// }

/* -------- Status badge (clickable) -------- */
function StatusBadge({ status, onClick, disabled }) {
  const map = {
    pending: "badge amber as-button",
    complete: "badge green as-button",
  };
  return (
    <button
      className={map[status] || "badge gray as-button"}
      onClick={disabled ? undefined : onClick}
      title={
        disabled
          ? "Cannot reverse the withdrawal"
          : "Click to make a withdrawal"
      }
    >
      {status === "complete" ? "Complete" : "Pending"}
    </button>
  );
}

/* -------- small helper to build query string -------- */
function qs(params) {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      sp.set(k, v);
    }
  });
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export default function Withdrawal() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [summary, setSummary] = useState({
    totalPayments: 0,
    completePayments: 0,
    pendingPayments: 0,
  });

  const [rows, setRows] = useState([]);

  // filters (auto-applied)
  const [role, setRole] = useState(""); // '', 'Traveler', 'Guide', 'Hotel Owner'
  const [dateFrom, setDateFrom] = useState(""); // YYYY-MM-DD
  const [dateTo, setDateTo] = useState(""); // YYYY-MM-DD
  const [fetching, setFetching] = useState(false);

  const handleDownloadReport = () => {
    // 1. Initialize in Landscape mode to fit all columns
    const doc = new jsPDF({ orientation: "landscape" });

    // Header
    doc.setFontSize(16);
    doc.text("Withdrawal Report", 14, 15);

    doc.setFontSize(10);
    doc.setTextColor(100);
    const dateStr = new Date().toLocaleString();
    doc.text(`Generated on: ${dateStr}`, 14, 22);

    let tableStartY = 28;
    if (dateFrom || dateTo) {
      const dateFilterText = `Date Range: ${dateFrom || 'N/A'} to ${dateTo || 'N/A'}`;
      doc.text(dateFilterText, 14, 26);
      tableStartY = 30;
    }

    let finalY = 25;

    // 2. Summary Table - Constrain width so it doesn't stretch across the whole landscape page
    autoTable(doc, {
      startY: tableStartY,
      head: [["Summary", "Amount"]],
      body: [
        ["Total Withdrawals", `LKR ${money(summary.totalPayments)}`],
        ["Paid Withdrawals", `LKR ${money(summary.completePayments)}`],
        ["Pending Withdrawals", `LKR ${money(summary.pendingPayments)}`],
      ],
      theme: "grid",
      styles: { fontSize: 10 },
      headStyles: { fillColor: [50, 50, 50] },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 60, halign: "right" },
      },
      didDrawPage: function (data) {
        finalY = data.cursor.y;
      },
    });

    const tableColumn = [
      "ID",
      "User Name",
      "Email",
      "Role",
      "Bank",
      "Branch",
      "Account No.",
      "Total",
      "Request",
      "Date",
      "Status",
    ];

    const tableRows = rows.map((item) => [
      item.regId,
      item.fullName,
      item.email,
      item.travelerAgentDetailsId ? `${item.role} Agent` : item.role,
      item.bankName || "-",
      item.bankBranch || "-",
      item.accountNumber || "-",
      `LKR ${money(item.fullAmount)}`,
      `LKR ${money(item.amount)}`,
      fmtDate(item.requestedAt),
      item.status,
    ]);

    // 3. Main Data Table - Smaller font, refined column widths
    autoTable(doc, {
      startY: finalY + 10,
      head: [tableColumn],
      body: tableRows,
      theme: "grid",
      styles: {
        fontSize: 8, // Smaller font to fit content
        cellPadding: 2,
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [41, 128, 185], // Professional blue header
        valign: "middle",
        halign: "center",
      },
      columnStyles: {
        0: { cellWidth: 20 }, // ID
        1: { cellWidth: 25 }, // Name
        2: { cellWidth: 40 }, // Email (needs space)
        3: { cellWidth: 20 }, // Role
        4: { cellWidth: 20 }, // Bank
        5: { cellWidth: 20 }, // Branch
        6: { cellWidth: 25 }, // Account
        7: { halign: "right", cellWidth: 25 }, // Total
        8: { halign: "right", cellWidth: 25 }, // Request
        9: { cellWidth: 30 }, // Date
        10: { cellWidth: 20, halign: "center" }, // Status
      },
      // Ensure the table uses the full width of the landscape page
      margin: { top: 20, left: 14, right: 14 },
    });

    doc.save("withdrawal-report.pdf");
  };

  const cards = useMemo(
    () => [
      {
        title: "Total Withdrawals",
        value: summary.totalPayments,
        sub: "All payout requests",
        icon: "🧾",
        prefix: "LKR ",
        money: true,
      },
      {
        title: "Paid Withdrawals",
        value: summary.completePayments,
        sub: "Completed Payouts",
        icon: "✅",
        prefix: "LKR ",
        money: true,
      },
      {
        title: "Pending Withdrawals",
        value: summary.pendingPayments,
        sub: "Pending Payouts",
        icon: "⏳",
        prefix: "LKR ",
        money: true,
      },
    ],
    [summary]
  );

  // initial load (no filters)
  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);
        setErr("");

        const [sumRes, listRes] = await Promise.all([
          fetch(`${API}/admin/withdrawals/summary`, { headers: getAdminHeaders() }),
          fetch(`${API}/admin/withdrawals`, { headers: getAdminHeaders() }),
        ]);
        const sum = await sumRes.json();
        const list = await listRes.json();

        if (!sumRes.ok)
          throw new Error(sum?.message || "Failed to fetch summary.");
        if (!listRes.ok)
          throw new Error(list?.message || "Failed to fetch list.");

        if (!mounted) return;
        setSummary({
          totalPayments: Number(sum.totalPayments) || 0,
          completePayments: Number(sum.completePayments) || 0,
          pendingPayments: Number(sum.pendingPayments) || 0,
        });
        setRows(Array.isArray(list) ? list : []);
      } catch (e) {
        if (mounted) setErr(e.message || "Load failed");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, []);

  // auto-apply filters whenever role/dateFrom/dateTo change
  useEffect(() => {
    // Skip while initial page is still loading
    if (loading) return;

    const controller = new AbortController();
    const run = async () => {
      try {
        setFetching(true);
        setErr("");

        const params = { role, date_from: dateFrom, date_to: dateTo };
        const query = qs(params);

        const [sumRes, listRes] = await Promise.all([
          fetch(`${API}/admin/withdrawals/summary${query}`, {
            headers: getAdminHeaders(),
            signal: controller.signal,
          }),
          fetch(`${API}/admin/withdrawals${query}`, {
            headers: getAdminHeaders(),
            signal: controller.signal,
          }),
        ]);
        const sum = await sumRes.json();
        const list = await listRes.json();

        if (!sumRes.ok)
          throw new Error(sum?.message || "Failed to fetch summary.");
        if (!listRes.ok)
          throw new Error(list?.message || "Failed to fetch list.");

        setSummary({
          totalPayments: Number(sum.totalPayments) || 0,
          paidPayments: Number(sum.paidPayments) || 0,
          pendingPayments: Number(sum.pendingPayments) || 0,
        });
        setRows(Array.isArray(list) ? list : []);
      } catch (e) {
        if (e.name !== "AbortError")
          setErr(e.message || "Apply filters failed");
      } finally {
        setFetching(false);
      }
    };

    // small debounce so rapid date changes don't spam the API
    const t = setTimeout(run, 150);
    return () => {
      controller.abort();
      clearTimeout(t);
    };
  }, [role, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  async function toggleStatus(payoutId) {
    const row = rows.find((r) => r.payoutId === payoutId);
    if (!row || row.status === "complete") return;

    // Custom HTML dropdown
    const { value: paymentMethod } = await Swal.fire({
      title: "Select Payment Method",
      html: `
        <div style="text-align: left;">
          <label for="payment-method" style="font-weight: 600; margin-bottom: 8px; display: block;">
            Choose your preferred payment option:
          </label>
          <select id="payment-method" class="modern-select">
            <option value="">-- Select --</option>
            <option value="bank_transfer">🏦 Direct Bank Transfer</option>
            <option value="koko">💳 KOKO Payout</option>
          </select>
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Proceed",
      cancelButtonText: "Cancel",
      customClass: {
        confirmButton: "custom-swal-confirm",
        cancelButton: "custom-swal-cancel",
        popup: "custom-swal-popup",
      },
      preConfirm: () => {
        const value = document.getElementById("payment-method").value;
        if (!value) {
          Swal.showValidationMessage("Please select a payment method");
        }
        return value;
      },
    });

    if (!paymentMethod) return;

    try {
      const isBankTransfer = paymentMethod === "bank_transfer";
      const endpoint = isBankTransfer
        ? `${API}/admin/withdrawals/${payoutId}/status`
        : `${API}/admin/withdrawals/${payoutId}/pay-koko`;

      const res = await fetch(endpoint, {
        method: isBankTransfer ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: isBankTransfer ? JSON.stringify({ status: "complete" }) : undefined,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Payment failed.");

      await Swal.fire({
        title: "Withdrawal Successful!",
        text: `Transaction ID: ${data.transactionId}`,
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });

      setRows((prev) =>
        prev.map((r) => (r.payoutId === payoutId ? { ...r, ...data.row } : r))
      );
      setSummary({
        totalPayments: Number(data.summary.totalPayments) || 0,
        completePayments: Number(data.summary.completePayments) || 0,
        pendingPayments: Number(data.summary.pendingPayments) || 0,
      });
    } catch (e) {
      await Swal.fire({
        title: "Payment Failed",
        text: e.message || "Withdrawal is not completed!",
        icon: "error",
        confirmButtonText: "Try Again",
        customClass: {
          confirmButton: "custom-swal-cancel",
        },
        buttonsStyling: false,
      });
    }
  }

  if (loading) {
    return (
      <div className="wd-page">
        <div className="wd-head">
          <div>
            <h2 className="wd-title">Withdrawal Requests</h2>
            <p className="muted">Loading…</p>
          </div>
        </div>
        <section className="wd-cards">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="card stat" style={{ opacity: 0.6 }}>
              <div className="stat-top">
                <div className="stat-title">Loading…</div>
                <div className="lock" aria-hidden>
                  …
                </div>
              </div>
              <div className="stat-value">—</div>
              <div className="stat-sub">—</div>
            </div>
          ))}
        </section>
        <section
          className="card table-card"
          style={{ padding: 24, color: "gray" }}
        >
          Loading withdrawals…
        </section>
      </div>
    );
  }

  if (err) {
    return (
      <div className="wd-page">
        <div className="wd-head">
          <div>
            <h2 className="wd-title">Withdrawal Requests</h2>
            <p className="muted" style={{ color: "crimson" }}>
              {err}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wd-page">
      {/* header */}
      <div className="wd-head">
        <div>
          <h2 className="wd-title">Withdrawal Requests</h2>
          <p className="muted">
            Manage payout requests from hotel owners, guides, and travelers
          </p>
        </div>
      </div>

      {/* stat cards (counts) */}
      <section className="wd-cards">
        {cards.map((s, i) => (
          <div key={i} className="card stat">
            <div className="stat-top">
              <div className="stat-title">{s.title}</div>
              <div className="lock" aria-hidden>
                {s.icon}
              </div>
            </div>
            <div className="stat-value">
              <CountUp
                to={s.value}
                prefix={s.prefix || ""}
                money={s.money || false}
              />
            </div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </section>

      {/* toolbar with auto-applied filters (NO buttons) */}
      <div className="wd-toolbar" style={{ gap: 8 }}>
        <h3 className="table-title">Payout requests</h3>

        <div
          className="filters"
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={handleDownloadReport}
            className="input"
            style={{
              padding: "8px 10px",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              cursor: "pointer",
            }}
          >
            Download Report
          </button>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="input"
            style={{
              padding: "8px 10px",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              color: role ? "inherit" : "var(--muted)",
            }}
            title="Filter by role"
          >
            <option value="">All Roles</option>
            <option value="Traveler">Traveler</option>
            <option value="Guide">Guide</option>
            <option value="Hotel Owner">Hotel Owner</option>
          </select>

          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="input"
            title="From (requested date)"
            style={{
              padding: "8px 10px",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              color: dateTo ? "inherit" : "var(--muted)",
            }}
          />
          <span className="muted">to</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="input"
            title="To (requested date)"
            style={{
              padding: "8px 10px",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              color: dateTo ? "inherit" : "var(--muted)",
            }}
          />

          {fetching ? (
            <span className="muted" style={{ fontSize: 12 }}>
              Updating…
            </span>
          ) : null}
        </div>
      </div>

      {/* table */}
      <section className="card table-card">
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 34 }}>
                <input type="checkbox" aria-label="select all" />
              </th>
              <th>Withdrawal ID</th>
              <th>User Name</th>
              <th>Email</th>
              <th>User Role</th>
              <th>Bank Name</th>
              <th>Branch</th>
              <th>ACC Number</th>
              <th>Total Amount</th>
              <th>Request Amount</th>
              <th>Date of Request</th>
              <th style={{ width: 140 }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.payoutId}>
                <td>
                  <input type="checkbox" aria-label={`select ${r.regId}`} />
                </td>
                <td>
                  <span className="id-badge">{r.regId}</span>
                </td>
                <td>{r.fullName}</td>
                <td className="muted">{r.email}</td>
                <td className="muted">
                  {r.travelerAgentDetailsId ? `${r.role} Agent` : r.role}
                </td>
                <td className="muted">{r.bankName || "N/A"}</td>
                <td className="muted">{r.bankBranch || "N/A"}</td>
                {/* <td className="muted">{maskAccountNumber(r.accountNumber)}</td> */}
                <td className="muted">{r.accountNumber || "N/A"}</td>
                <td className="amount">LKR {money(r.fullAmount)}</td>
                <td className="amount">LKR {money(r.amount)}</td>
                <td className="muted nowrap">{fmtDate(r.requestedAt)}</td>
                <td>
                  <StatusBadge
                    status={r.status}
                    onClick={() => toggleStatus(r.payoutId)}
                    disabled={r.status === "complete"}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
